import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShoppingBag, RotateCcw, Loader2, Store, Truck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const STATUS = {
  pending: { lv: 'Gaida apstiprinājumu', cls: 'bg-amber-100 text-amber-700' },
  confirmed: { lv: 'Apstiprināts', cls: 'bg-blue-100 text-blue-700' },
  preparing: { lv: 'Tiek gatavots', cls: 'bg-blue-100 text-blue-700' },
  ready: { lv: 'Gatavs', cls: 'bg-emerald-100 text-emerald-700' },
  completed: { lv: 'Pabeigts', cls: 'bg-emerald-100 text-emerald-700' },
  rejected: { lv: 'Noraidīts', cls: 'bg-red-100 text-red-700' },
  cancelled: { lv: 'Atcelts', cls: 'bg-gray-100 text-gray-600' },
};

export default function OrderHistorySection() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/profile/orders`)
      .then(({ data }) => setOrders(data.orders || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const reorder = (o) => {
    if (!o.cart_raw || !o.cart_raw.length) { toast.error('Šo pasūtījumu nevar atkārtot'); return; }
    try { sessionStorage.setItem('kolv_cart_reorder', JSON.stringify({ pageId: o.page_id, cartRaw: o.cart_raw })); } catch { /* ignore */ }
    navigate(`/uznemums/${o.business_user_id}/${o.page_id}`);
  };

  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 sm:p-8 mb-5" data-testid="menu-orders-section">
      <div className="flex items-center gap-2 mb-1">
        <ShoppingBag className="w-5 h-5 text-[#D97736]" />
        <h2 className="font-outfit text-xl font-semibold text-[#121212]">Mani pasūtījumi</h2>
      </div>
      <p className="text-sm text-[#5C5C5C] mb-5">Ēdienu un preču pasūtījumi uzņēmumu lapās.</p>

      {loading ? (
        <div className="flex items-center gap-2 text-[#9CA3AF] text-sm" data-testid="orders-loading"><Loader2 className="w-4 h-4 animate-spin" /> Ielādē...</div>
      ) : orders.length === 0 ? (
        <p className="text-sm text-[#9CA3AF]" data-testid="no-menu-orders">Vēl nav pasūtījumu.</p>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const st = STATUS[o.status] || { lv: o.status || '—', cls: 'bg-gray-100 text-gray-600' };
            return (
              <div key={o.code} className="rounded-xl border border-[#F3F2EE] p-4" data-testid={`menu-order-${o.code}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#121212] flex items-center gap-1.5">{o.business_name}</p>
                    <p className="text-xs text-[#9CA3AF] mt-0.5">
                      {(o.created_at || '').slice(0, 10)} · {o.code}
                      {o.fulfillment && <span className="inline-flex items-center gap-1 ml-1">· {o.fulfillment === 'delivery' ? <><Truck className="w-3 h-3" /> Piegāde</> : <><Store className="w-3 h-3" /> Saņemšana</>}</span>}
                    </p>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${st.cls}`} data-testid={`order-status-${o.code}`}>{st.lv}</span>
                </div>
                <div className="mt-2 text-sm text-[#5C5C5C]">
                  {(o.items || []).map((it, i) => (
                    <span key={i}>{it.qty}× {it.name}{i < o.items.length - 1 ? ', ' : ''}</span>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-[#121212]">{o.amount != null ? `€${Number(o.amount).toFixed(2)}` : ''}</span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => navigate(`/pasutijums/${o.code}`)} className="text-sm text-[#5C5C5C] hover:text-[#121212] font-medium px-3 py-1.5" data-testid={`track-order-${o.code}`}>Statuss</button>
                    <button onClick={() => reorder(o)} disabled={!o.cart_raw || !o.cart_raw.length}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-[#D97736] hover:bg-[#c2652b] disabled:opacity-40 px-3.5 py-1.5 rounded-full transition-colors"
                      data-testid={`reorder-${o.code}`}>
                      <RotateCcw className="w-3.5 h-3.5" /> Pasūtīt vēlreiz
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
